/**
 * applyImportState — AK-368c re-entrancy-safe reducer prev→next для importStore
 * → императивные вызовы ImportController.
 *
 * Та же P0-грабля, что в measure/editor/addField (ADR-038): side-effect'ы
 * контроллера могут СИНХРОННО дёрнуть стор (например через zustand set в
 * обработчике), что снова триггерит подписку → повторный applyImportState().
 * Если prev-снапшот обновить ПОСЛЕ side-effect'ов, вложенный вызов увидит старое
 * состояние → повторит тот же side-effect → рекурсия.
 *
 * Решение (как в applyAddFieldState): захватываем старый prev в локали, СРАЗУ
 * перезаписываем prev новым next, и ТОЛЬКО ПОТОМ дёргаем side-effect'ы —
 * вложенный apply увидит prev == next → no-op.
 *
 * Что рисуем:
 *   - phase preview + rows изменились → showPreview (+ fit на ПЕРВОМ показе);
 *   - highlightIndex/editingIndex изменились → showPreview с подсветкой/приглушением;
 *   - phase ушла из preview (saving/idle/error) → clear.
 */
import type { ImportPhase, ImportRow } from './importStore';
import type { ImportRenderFeature } from './ImportController';

/** Срез стора, который читает reducer. */
export interface ImportSnapshot {
  phase: ImportPhase;
  rows: ImportRow[];
  highlightIndex: number | null;
  editingIndex: number | null;
}

/** Мутируемый prev-снапшот (по ссылке между вызовами). */
export interface ImportPrevRef {
  phase: ImportPhase;
  /** Ссылка на массив rows — сравниваем по identity (zustand возвращает новый на set). */
  rows: ImportRow[] | null;
  highlightIndex: number | null;
  editingIndex: number | null;
  /** Уже делали fitBounds для текущей пачки? (fit только на первом показе). */
  didFit: boolean;
}

/** Императивная поверхность контроллера, которую дёргает reducer. */
export interface ImportControllerLike {
  showPreview(
    features: readonly ImportRenderFeature[],
    highlightIndex: number | null,
    dimExcept: number | null,
  ): void;
  fitToPreview(features: readonly ImportRenderFeature[]): void;
  clear(): void;
}

export function initImportPrev(): ImportPrevRef {
  return {
    phase: 'idle',
    rows: null,
    highlightIndex: null,
    editingIndex: null,
    didFit: false,
  };
}

/** rows → render-фичи (то, что понимает контроллер). */
function toRenderFeatures(rows: readonly ImportRow[]): ImportRenderFeature[] {
  return rows.map((r) => ({
    index: r.index,
    geometry: r.geometry,
    valid: r.valid,
    selected: r.selected,
  }));
}

export function applyImportState(
  prev: ImportPrevRef,
  next: ImportSnapshot,
  ctrl: ImportControllerLike,
): void {
  const pPhase = prev.phase;
  const pRows = prev.rows;
  const pHighlight = prev.highlightIndex;
  const pEditing = prev.editingIndex;
  const pDidFit = prev.didFit;

  // Снимок ДО side-effect'ов: re-entrant apply увидит уже актуальные prev*.
  prev.phase = next.phase;
  prev.rows = next.rows;
  prev.highlightIndex = next.highlightIndex;
  prev.editingIndex = next.editingIndex;

  // Ушли из превью (сохранение / сброс / ошибка) → чистим оверлей + сбрасываем fit.
  if (next.phase !== 'preview') {
    prev.didFit = false;
    if (pPhase === 'preview') ctrl.clear();
    return;
  }

  // В превью. Нужно ли перерисовать? — при входе в фазу, смене пачки (identity),
  // смене подсветки или режима правки.
  const rowsChanged = next.rows !== pRows;
  const highlightChanged = next.highlightIndex !== pHighlight;
  const editingChanged = next.editingIndex !== pEditing;
  const enteredPreview = pPhase !== 'preview';
  if (!rowsChanged && !highlightChanged && !editingChanged && !enteredPreview) {
    return;
  }

  const features = toRenderFeatures(next.rows);
  ctrl.showPreview(features, next.highlightIndex, next.editingIndex);

  // fitBounds — один раз на пачку (первый вход в превью). Флаг сбрасывается,
  // когда уходим из превью (выше).
  if (enteredPreview && !pDidFit) {
    prev.didFit = true;
    ctrl.fitToPreview(features);
  }
}
